// micro copy store, which should ideally work the same as in global website
// but which needed to be converted from zustand store to redux store to work in the booking engine project
// The goal is to allow loading 'microCopy' and 'MicroCopyBundles' from contentful and storing them here
// Should be smart enough to allow swapping between languages in the ui and only having the copy load for each language once

export interface MicroCopy {
    key: string;
    value: Record<string, string>;
}

export interface MicroCopyState {
    microCopyMap: Record<string, MicroCopy>;
    microCopyBundles: Record<string, string[]>; // Now uses array instead of Set
}


export const ADD_MICROCOPY = 'ADD_MICROCOPY';
export const ADD_MICROCOPY_BUNDLE = 'ADD_MICROCOPY_BUNDLE';
export const UPDATE_MICROCOPY = 'UPDATE_MICROCOPY';

interface AddMicroCopyAction {
    type: typeof ADD_MICROCOPY;
    payload: MicroCopy;
}

interface AddMicroCopyBundleAction {
    type: typeof ADD_MICROCOPY_BUNDLE;
    bundleKey: string;
    items: MicroCopy[];
    lang: string;
}

interface UpdateMicroCopyAction {
    type: typeof UPDATE_MICROCOPY;
    key: string;
    lang: string;
    text: string;
}

export type MicroCopyActions =
    | AddMicroCopyAction
    | AddMicroCopyBundleAction
    | UpdateMicroCopyAction;

export const microCopyActionCreators = {
    addMicroCopy: (newCopy: MicroCopy): AddMicroCopyAction => ({
        type: ADD_MICROCOPY,
        payload: newCopy,
    }),

    addMicroCopyBundle: (
        bundleKey: string,
        items: MicroCopy[],
        lang: string
    ): AddMicroCopyBundleAction => ({
        type: ADD_MICROCOPY_BUNDLE,
        bundleKey,
        items,
        lang,
    }),

    updateMicroCopy: (
        key: string,
        lang: string,
        text: string
    ): UpdateMicroCopyAction => ({
        type: UPDATE_MICROCOPY,
        key,
        lang,
        text,
    }),
};


const initialState: MicroCopyState = {
    microCopyMap: {},
    microCopyBundles: {},
};

export const reducer = (
    state = initialState,
    action: MicroCopyActions
): MicroCopyState => {
    switch (action.type) {
        case ADD_MICROCOPY: {
            const newCopy = action.payload;
            if (Object.keys(newCopy.value).length === 0) return state;

            const existing = state.microCopyMap[newCopy.key] || { key: newCopy.key, value: {} };

            return {
                ...state,
                microCopyMap: {
                    ...state.microCopyMap,
                    [newCopy.key]: {
                        ...existing,
                        value: { ...existing.value, ...newCopy.value },
                    },
                },
            };
        }

        case ADD_MICROCOPY_BUNDLE: {
            const { bundleKey, items, lang } = action;
            const updatedMap = { ...state.microCopyMap };

            items.forEach(item => {
                const existing = updatedMap[item.key] || { key: item.key, value: {} };
                updatedMap[item.key] = {
                    ...existing,
                    value: { ...existing.value, [lang]: item.value[lang] },
                };
            });

            const existingLangs = state.microCopyBundles[bundleKey] || [];
            const updatedLangs = existingLangs.includes(lang)
                ? existingLangs
                : [...existingLangs, lang];

            return {
                ...state,
                microCopyMap: updatedMap,
                microCopyBundles: {
                    ...state.microCopyBundles,
                    [bundleKey]: updatedLangs,
                },
            };
        }

        case UPDATE_MICROCOPY: {
            const { key, lang, text } = action;
            const existing = state.microCopyMap[key];
            if (!existing) return state;

            return {
                ...state,
                microCopyMap: {
                    ...state.microCopyMap,
                    [key]: {
                        ...existing,
                        value: { ...existing.value, [lang]: text },
                    },
                },
            };
        }

        default:
            return state;
    }
};


// Helper functions
export const getMicroCopyValue = (
    state: MicroCopyState,
    key: string,
    lang: string
): string | undefined => state.microCopyMap[key]?.value?.[lang];

export const hasBundle = (
    state: MicroCopyState,
    bundleKey: string,
    lang: string
): boolean => state.microCopyBundles[bundleKey]?.includes(lang) ?? false;
